import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { toast } from "sonner";
import { Trash2, FileText, Pencil, Check, X } from "lucide-react";
import { API, resolveImage } from "../../data/site";
import { inputCls, DropZone, AdminButton } from "../ui";

const CATEGORIES = ["General", "Homes for Sale", "Floor Plans", "Projects", "About / Mark", "Homepage", "Other"];

const MediaPanel = () => {
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [editing, setEditing] = useState(null); // {id, name, alt, category}
  const { data: items = [], refetch } = useQuery({
    queryKey: ["media", search, category],
    queryFn: async () =>
      (await axios.get(`${API}/media`, { params: { search: search || undefined, category: category || undefined }, withCredentials: true })).data,
  });

  const saveEdit = async () => {
    await axios.patch(`${API}/media/${editing.id}`, { name: editing.name, alt: editing.alt, category: editing.category }, { withCredentials: true });
    toast.success("Image details saved");
    setEditing(null);
    refetch();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this file? It will no longer be available on the website.")) return;
    await axios.delete(`${API}/media/${id}`, { withCredentials: true });
    toast.success("Deleted");
    refetch();
  };

  return (
    <div data-testid="media-panel">
      <div className="flex flex-wrap gap-3 mb-4">
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name or alt text…" data-testid="media-search"
          className={`${inputCls} flex-1 min-w-[220px]`} />
        <select value={category} onChange={(e) => setCategory(e.target.value)} className={`${inputCls} w-auto`} data-testid="media-category-filter">
          <option value="">All Categories</option>
          {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
        </select>
      </div>
      <DropZone onUploaded={() => refetch()} accept="image/*,.pdf" category={category || "General"} label="Drag & drop images here, or click to browse — automatically optimized for the web" testid="media-dropzone" />

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mt-8" data-testid="media-grid">
        {items.map((m) => {
          const url = resolveImage(`/api/files/${m.thumb_path || m.storage_path}`);
          const full = resolveImage(`/api/files/${m.storage_path}`);
          const isPdf = (m.content_type || "").includes("pdf");
          const isEditing = editing?.id === m.id;
          return (
            <div key={m.id} className="bg-white border border-[rgba(26,26,26,0.1)] overflow-hidden" data-testid={`media-item-${m.id}`}>
              <a href={full} target="_blank" rel="noreferrer" className="block aspect-[4/3] bg-[#EFECE7] flex items-center justify-center">
                {isPdf ? <FileText size={36} className="text-[#8C6A4F]" strokeWidth={1.2} /> : <img src={url} alt={m.alt || m.name} className="w-full h-full object-cover" loading="lazy" />}
              </a>
              <div className="p-3">
                {isEditing ? (
                  <div className="flex flex-col gap-2">
                    <input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className={inputCls} placeholder="Name" data-testid="media-edit-name" />
                    <input value={editing.alt} onChange={(e) => setEditing({ ...editing, alt: e.target.value })} className={inputCls} placeholder="Alt text (for SEO)" data-testid="media-edit-alt" />
                    <select value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} className={inputCls} data-testid="media-edit-category">
                      {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                    </select>
                    <div className="flex gap-2">
                      <button onClick={saveEdit} className="p-1.5 text-[#4E735B]" data-testid="media-edit-save" aria-label="Save"><Check size={15} /></button>
                      <button onClick={() => setEditing(null)} className="p-1.5 text-[#A3A09B]" aria-label="Cancel"><X size={15} /></button>
                    </div>
                  </div>
                ) : (
                  <>
                    <p className="text-xs font-semibold truncate">{m.name || m.original_filename}</p>
                    <p className="text-[0.62rem] text-[#A3A09B] font-mono uppercase tracking-wider mt-0.5">{m.category || "General"}</p>
                    {m.alt && <p className="text-[0.62rem] text-[#5C5A56] truncate mt-0.5" title={m.alt}>alt: {m.alt}</p>}
                    <div className="flex gap-1 mt-2">
                      <button onClick={() => setEditing({ id: m.id, name: m.name || "", alt: m.alt || "", category: m.category || "General" })} className="p-1.5 hover:text-[#8C6A4F]" data-testid={`media-edit-${m.id}`} aria-label="Edit"><Pencil size={13} /></button>
                      <button onClick={() => remove(m.id)} className="p-1.5 hover:text-red-600" data-testid={`media-delete-${m.id}`} aria-label="Delete"><Trash2 size={13} /></button>
                    </div>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {items.length === 0 && <p className="py-10 text-[#A3A09B] font-mono text-xs tracking-[0.2em] uppercase">No files match.</p>}
    </div>
  );
};

export default MediaPanel;
